import type { GetContextMenuItemsParams, IRowNode, MenuItemDef } from 'ag-grid-community';
import { compact, get } from 'lodash-es';
import { ModeEnum } from '../../types';
import { EMPTY_ARRAY } from '../../utils';
import type { AgGridMenuItems } from '../BlotterTable';
import { IconName } from '../Icons';
import type { EntityAdminClass } from './EntityAdminClass';
import type {
  EntityAdminModeSwitchConfiguration,
  EntityAdminRecord,
  HierarchicalColumnProps,
  RightPinnedButtonProps,
} from './types';

export interface EntityAdminSelectionActionState<TRecord extends EntityAdminRecord> {
  showEdit: boolean;
  showModeSwitch: boolean;
  showDelete: boolean;
  showAny: boolean;
  editDisabled: boolean;
  enableDisabled: boolean;
  disableDisabled: boolean;
  deleteDisabled: boolean;
  deletableEntities: EntityAdminClass<TRecord>[];
  editableEntities: EntityAdminClass<TRecord>[];
  modeSwitchableEntities: EntityAdminClass<TRecord>[];
  editLabel: string;
  enableLabel: string;
  disableLabel: string;
  deleteLabel: string;
}

export interface EntityAdminSelectionActionParams<TRecord extends EntityAdminRecord> {
  entities: EntityAdminClass<TRecord>[];
  allowBulkEdit?: boolean;
  allowEditEntity?: boolean | RightPinnedButtonProps<TRecord>;
  allowDeleteEntity?: boolean | RightPinnedButtonProps<TRecord>;
  suppressBulkDelete?: boolean;
  bulkEditFields?: readonly string[];
  /** Present only when mode switching is enabled. `modeField` is always resolved. */
  modeSwitch?: EntityAdminModeSwitchConfiguration<TRecord>;
  maxBulkEditRows: number;
  isRequesting: boolean;
}

export interface EntityAdminSelectionContextMenuConfig<TRecord extends EntityAdminRecord> extends Omit<
  EntityAdminSelectionActionParams<TRecord>,
  'entities'
> {
  openEntityAdminDrawer: HierarchicalColumnProps<TRecord>['openEntityAdminDrawer'];
  updateClassEntitiesWithConfirmation: (
    classEntities: EntityAdminClass<TRecord>[],
    newEntity: EntityAdminRecord
  ) => void;
  deleteClassEntitiesWithConfirmation: (classEntities: EntityAdminClass<TRecord>[]) => void;
}

export function getEntityAdminDeletableEntities<TRecord extends EntityAdminRecord>(
  entities: EntityAdminClass<TRecord>[],
  allowDeleteEntity: EntityAdminSelectionActionParams<TRecord>['allowDeleteEntity']
): { visible: EntityAdminClass<TRecord>[]; deletable: EntityAdminClass<TRecord>[] } {
  if (allowDeleteEntity == null || allowDeleteEntity === false) {
    return { visible: EMPTY_ARRAY, deletable: EMPTY_ARRAY };
  }
  if (allowDeleteEntity === true) {
    return { visible: entities, deletable: entities };
  }
  const visible = entities.filter(({ data }) => !allowDeleteEntity.isHidden?.(data));
  const deletable = visible.filter(({ data }) => !allowDeleteEntity.isDisabled?.(data));
  return { visible, deletable };
}

function getEditableEntities<TRecord extends EntityAdminRecord>(
  entities: EntityAdminClass<TRecord>[],
  allowEditEntity: EntityAdminSelectionActionParams<TRecord>['allowEditEntity']
): { visible: EntityAdminClass<TRecord>[]; editable: EntityAdminClass<TRecord>[] } {
  if (allowEditEntity == null || allowEditEntity === false) {
    return { visible: EMPTY_ARRAY, editable: EMPTY_ARRAY };
  }
  if (allowEditEntity === true) {
    return { visible: entities, editable: entities };
  }
  const visible = entities.filter(({ data }) => !allowEditEntity.isHidden?.(data));
  const editable = visible.filter(({ data }) => !allowEditEntity.isDisabled?.(data));
  return { visible, editable };
}

export function getEntityAdminSelectionActionState<TRecord extends EntityAdminRecord>(
  params: EntityAdminSelectionActionParams<TRecord>
): EntityAdminSelectionActionState<TRecord> {
  const {
    entities,
    allowBulkEdit,
    allowEditEntity,
    allowDeleteEntity,
    suppressBulkDelete,
    bulkEditFields,
    modeSwitch,
    maxBulkEditRows,
    isRequesting,
  } = params;

  const isSingleRow = entities.length === 1;
  const showBulkEditToolbar = allowBulkEdit === true && allowEditEntity !== false;
  const { visible: visibleForEdit, editable: editableEntities } = getEditableEntities(entities, allowEditEntity);
  const showEdit =
    visibleForEdit.length > 0 && (isSingleRow || (showBulkEditToolbar && (bulkEditFields?.length ?? 0) >= 1));
  const visibleForModeSwitch = entities.filter(({ data }) => !modeSwitch?.isHidden?.(data));
  const modeSwitchableEntities = visibleForModeSwitch.filter(({ data }) => !modeSwitch?.isDisabled?.(data));
  const showModeSwitch = showBulkEditToolbar && modeSwitch != null && visibleForModeSwitch.length > 0;
  const modeField = modeSwitch?.modeField;
  const { visible: visibleForDelete, deletable: deletableEntities } = getEntityAdminDeletableEntities(
    entities,
    allowDeleteEntity
  );
  const showDelete =
    (allowBulkEdit === true || isSingleRow) &&
    allowDeleteEntity != null &&
    allowDeleteEntity !== false &&
    !suppressBulkDelete &&
    visibleForDelete.length > 0;
  const targetNoun = entities.length === 1 ? 'Row' : 'Selection';
  const disabledDueToMaxRows = entities.length > maxBulkEditRows;
  const disabledDueToRequesting = isRequesting;

  return {
    showEdit,
    showModeSwitch,
    showDelete,
    showAny: showEdit || showModeSwitch || showDelete,
    editDisabled: disabledDueToMaxRows || disabledDueToRequesting || editableEntities.length === 0,
    enableDisabled:
      disabledDueToMaxRows ||
      disabledDueToRequesting ||
      modeSwitchableEntities.length === 0 ||
      modeField == null ||
      modeSwitchableEntities.every(entity => (get(entity.data, modeField) as ModeEnum) === ModeEnum.Enabled),
    disableDisabled:
      disabledDueToMaxRows ||
      disabledDueToRequesting ||
      modeSwitchableEntities.length === 0 ||
      modeField == null ||
      modeSwitchableEntities.every(entity => (get(entity.data, modeField) as ModeEnum) === ModeEnum.Disabled),
    deleteDisabled: deletableEntities.length === 0,
    deletableEntities,
    editableEntities,
    modeSwitchableEntities,
    editLabel: `Edit ${targetNoun}`,
    enableLabel: `Enable ${targetNoun}`,
    disableLabel: `Disable ${targetNoun}`,
    deleteLabel: `Delete ${targetNoun}`,
  };
}

export function getEntitiesForEntityAdminContextMenu<TRecord extends EntityAdminRecord>(
  params: GetContextMenuItemsParams<EntityAdminClass<TRecord>>
): EntityAdminClass<TRecord>[] {
  const clicked = params.node?.data;
  const selected = compact(getSelectedEntityAdminNodes(params).map(node => node.data));
  if (clicked != null && selected.includes(clicked)) {
    return selected;
  }
  return clicked != null ? [clicked] : selected;
}

function getSelectedEntityAdminNodes<TRecord extends EntityAdminRecord>(
  params: Pick<GetContextMenuItemsParams<EntityAdminClass<TRecord>>, 'api'>
): IRowNode<EntityAdminClass<TRecord>>[] {
  const nodes: IRowNode<EntityAdminClass<TRecord>>[] = [];
  params.api.forEachNodeAfterFilter(node => {
    // Tree parents with children are AG Grid group nodes, but they are still editable entities.
    if (node.data != null && node.isSelected()) {
      nodes.push(node);
    }
  });
  return nodes;
}

function agGridMenuIcon(iconName: IconName): string {
  return `<i class="ag-icon ${iconName}"/>`;
}

export function getEntityAdminSelectionContextMenuItems<TRecord extends EntityAdminRecord>(
  params: GetContextMenuItemsParams<EntityAdminClass<TRecord>>,
  config: EntityAdminSelectionContextMenuConfig<TRecord>
): AgGridMenuItems<EntityAdminClass<TRecord>> {
  const entities = getEntitiesForEntityAdminContextMenu(params);
  const state = getEntityAdminSelectionActionState({ ...config, entities });
  if (!state.showAny) {
    return EMPTY_ARRAY;
  }

  const modeField = config.modeSwitch?.modeField;

  const items: MenuItemDef<EntityAdminClass<TRecord>>[] = compact([
    state.showEdit
      ? {
          name: state.editLabel,
          action: () =>
            config.openEntityAdminDrawer({ setupEntities: state.editableEntities, addingChildEntity: false }),
          disabled: state.editDisabled,
          icon: agGridMenuIcon(IconName.Pencil),
        }
      : undefined,
    state.showModeSwitch && modeField != null
      ? {
          name: state.enableLabel,
          action: () =>
            config.updateClassEntitiesWithConfirmation(state.modeSwitchableEntities, {
              [modeField]: ModeEnum.Enabled,
            }),
          disabled: state.enableDisabled,
          icon: agGridMenuIcon(IconName.Check),
        }
      : undefined,
    state.showModeSwitch && modeField != null
      ? {
          name: state.disableLabel,
          action: () =>
            config.updateClassEntitiesWithConfirmation(state.modeSwitchableEntities, {
              [modeField]: ModeEnum.Disabled,
            }),
          disabled: state.disableDisabled,
          icon: agGridMenuIcon(IconName.Close),
        }
      : undefined,
    state.showDelete
      ? {
          name: state.deleteLabel,
          action: () => config.deleteClassEntitiesWithConfirmation(state.deletableEntities),
          disabled: state.deleteDisabled,
          icon: agGridMenuIcon(IconName.Trash),
        }
      : undefined,
  ]);

  return items;
}
